package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/produce_state")
@Composable
fun ProduceStatePage() {
    BlogLayout(
        PageProperties(
            "produceState",
            "produceState - (Converting External State into Compose State - Bringing Non-Compose State into Compose - Asynchronous to State - Converts suspend functions to State<T>)"
        ),
        BlogProperties("Jetpack Compose", "produceState"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "produceState - (Callbacks (Location) - Converting External State into Compose State - Bringing Non-Compose State into Compose - Asynchronous to State - Converts suspend functions to State<T>)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "GPS location, sensors, bluetooth, media player progress, Firebase listeners",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState helps us to run suspend functions inside a composable function and return a result as State<T> object whose value is produced by a new launched coroutine that Compose functions can automatically observe, recompose, and also cancel the coroutine scope when the composable leaves the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState isn’t just a shortcut for remember + LaunchedEffect. It’s a conflated state producer that combines state management, side-effects, and deterministic cleanup under one coroutine scope. It creates a state variable and then updates the value of the state variable based on the results of a side effect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The produceState function is used in Jetpack Compose to manage the production and consumption of state values. This function is used to return a coroutine scope along with a state value. The MutableState class represents a modifiable state value. This value is modified by the producer and read by the consumer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState folds those two pieces into one. It gives you a State and runs a coroutine where you assign into value, and it launches and cancels on the same lifecycle rules as LaunchedEffect.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState is a robust API that bridges the gap between non-Compose state and Compose-friendly State. Here’s how it works:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ol {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "It holds a result variable using remember { mutableStateOf(initialValue) }.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Runs the producer block inside a LaunchedEffect.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Updates the result state whenever the value is updated in the producer block.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "You can easily create your own effects building on top of the existing APIs like Customizing produceState.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It acts as a bridge between Compose and the data you want to fetch or calculate asynchronously. This is particularly useful for managing state that depends on asynchronous operations or when you need to convert non-Compose state into Compose state. produceState is used to convert asynchronous or external state, such as data from a network or database, into Compose state. (Fetching data from a repository and exposing it as a State that Composable can observe.Fetching data from a repository and exposing it as a State that Composable can observe). It launches a coroutine that updates the state as necessary. ProduceState is used to convert non-Compose state, such as Flow, LiveData, or RxJava, into Compose state. It launches a coroutine scoped to the composition and updates the State object based on the emitted.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Helps integrate external data sources or non-Composable state into the Composable state. It starts a coroutine that is tied to the composition, allowing it to send values to the returned state. This coroutine begins when produceState is first composed and stops when it is removed from the composition. The resulting state combines values, meaning that setting the same value won't trigger a recomposition",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState allows you to create a state in a Composable by running a suspendable block of code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This can be used to ensure that your composables are always displaying the latest data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It is run background thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Convert the non-Compose state into Compose state, and launches a coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "That managing state that depends on asynchronous operations, such as network requests.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState based on LaunchedEffect the implementation, the initialization, and the update logic of the state are further simplified.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A produceState(initialValue, keys, producer), Similar to LaunchedEffect launches on the first composition and on the keys change, but it can actually return a state, just like manipulating a state inside the LaunchedEffect, it requires an initialValue and can have multiple keys to re-execute or none at all. Here producer is a suspend block but also provides awaitDispose which finally terminates the effect, just like onDispose inDisposableEffect.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The produce state composable takes initialValue and producer. InitialValue is the initial value of the state variable. Producer is a lambda that defines the side effect that will be executed to update the value of the state variable. This lambda will be executed when the produce state composable is first composed, and it will be executed again whenever the value of the state variable changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "produceState signature",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    public fun <T> produceState(
                        initialValue: T,
                        vararg keys: Any?,
                        producer: suspend ProduceStateScope<T>.() -> Unit,
                    ): State<T> {
                        val result = remember { mutableStateOf(initialValue) }
                        @Suppress("CHANGING_ARGUMENTS_EXECUTION_ORDER_FOR_NAMED_VARARGS")
                        LaunchedEffect(keys = keys) { ProduceStateScopeImpl(result, coroutineContext).producer() }
                        return result
                    }
                    """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "initialValue: The initial value of the state before the producer starts emitting values.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "keys: Dependencies for the producer, if any of the keys change, then the producer coroutine will restart.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "producer: this is suspending lambda that update the state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "produceState Internal working",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    public fun <T> produceState(
                        initialValue: T,
                        vararg keys: Any?,
                        producer: suspend ProduceStateScope<T>.() -> Unit,
                    ): State<T> {
                        val result = remember { mutableStateOf(initialValue) }
                        @Suppress("CHANGING_ARGUMENTS_EXECUTION_ORDER_FOR_NAMED_VARARGS")
                        LaunchedEffect(keys = keys) { ProduceStateScopeImpl(result, coroutineContext).producer() }
                        return result
                    }

                    private class ProduceStateScopeImpl<T>(
                        state: MutableState<T>,
                        override val coroutineContext: CoroutineContext,
                    ) : ProduceStateScope<T>, MutableState<T> by state {

                        override suspend fun awaitDispose(onDispose: () -> Unit): Nothing {
                            try {
                                suspendCancellableCoroutine<Nothing> {}
                            } finally {
                                onDispose()
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose creates a MutableState<T> with initialValue.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launches a Coroutine Scope to the composable that runs the user producer suspend block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect starts a coroutine tied to the composition lifecycle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancel the coroutine scope automatically when a composable leaves compositions or key changes. This is handled in awaitDispose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val state by produceState(initialValue, key) {
                        value = fetchData(key)
                    }
                    """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "initialValue: the starting state (e.g. Loading).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "key: if this changes, the coroutine restarts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "value: assign new results here to update the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The coroutine is automatically cancelled when the composable is removed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun UserProfile(userId: String, viewModel: UserViewModel) {

                        val userState by produceState<User?>(initialValue = null, userId) {
                            value = viewModel.fetchUserFromNetwork(userId)
                        }

                        if (userState == null) {
                            Text("Loading...")
                        } else {
                            Text("User: ${userState?.name}")
                        }
                    }

                    suspend fun fetchUserFromNetwork(userId: String): User {
                        // Simulated network delay
                        delay(2000)
                        return User(name = "Kamlesh")
                    }

                    data class User(val name: String)
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState is used to create a State<User?> object with an initial value of null.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The producer coroutine fetches the user data asynchronously and updates the value of the state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the value changes, the UserProfile composable recomposes to reflect the updated data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    public fun <T> produceState( 
                        initialValue: T,
                        producer: suspend ProduceStateScope<T>. () → Unit,
                    ): State<T> {
                        val result = remember { mutableStateOf(initialValue) }
                        LaunchedEffect(Unit) { ProduceStateScopeImpl(result, coroutineContext).producer() } 
                        return result
                    }
                    
                    val userState by produceState<User?>(initialValue = null, userId) {
                        value = repository.loadUser(userId)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Flow
                    
                    @Composable
                    fun <T> Flow<T>.collectAsState(initial: T): State<T> {
                        return produceState(initial, this) {
                            collect { value = it }
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/pitampoudel/komposeauth.git
            KotlinCode(
                code = """
                    @Composable
                    fun rememberCurrentUser(): LazyState<ProfileResponse> {
                        val authPreferences = koinInject<AuthPreferences>()
                        return produceState<LazyState<ProfileResponse>>(LazyState.Loading) {
                            authPreferences.authenticatedUser.collectLatest { user ->
                                value = LazyState.Loaded(user)
                            }
                        }.value
                    }

                    internal interface AuthPreferences {
                        val authenticatedUser: Flow<ProfileResponse?>
                    }
                    
                    sealed class LazyState<out T> {
                        object Loading : LazyState<Nothing>()
                        data class Loaded<T>(val value: T?) : LazyState<T>()
                        val isLoading: Boolean get() = this is Loading
                        val valueOrNull: T? get() = (this as? Loaded)?.value
                        val valueOrThrow: T get() = (this as Loaded).value!!
                    }
                    
                    @Serializable
                    data class ProfileResponse(
                        @SerialName("givenName")
                        val givenName: String,
                        @SerialName("familyName")
                        val familyName: String?,
                        @SerialName("email")
                        val email: String,
                        @SerialName("phoneNumber")
                        val phoneNumber: String?,
                        @SerialName("emailVerified")
                        val emailVerified: Boolean,
                        @SerialName("phoneNumberVerified")
                        val phoneNumberVerified: Boolean,
                        @SerialName("kycVerified")
                        val kycVerified: Boolean = false,
                        @SerialName("picture")
                        val picture: String?,
                        @SerialName("id")
                        val id: String,
                        @SerialName("createdAt")
                        @Contextual val createdAt: Instant,
                        @SerialName("updatedAt")
                        @Contextual val updatedAt: Instant,
                        @SerialName("socialLinks")
                        val socialLinks: List<String>,
                        @SerialName("roles")
                        val roles: List<String>
                    ) {
                        fun fullName() = "${'$'}givenName ${'$'}familyName"
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Permission 
                    
                    //Don't
                    var hasPermission by remember { mutableStateOf<Boolean?>(null) }
                    LaunchedEffect (Unit) {
                        hasPermission = withContext(Dispatchers.I0) {
                            AppUtils.hasRequiredStoragePermission(LocalContext.current)
                        }
                    }
                    
                    //Instead Do
                    val hasPermission by produceState<Boolean?>(initialValue = null) {
                        value = withContext(Dispatchers.I0) {
                            AppUtils.hasRequiredStoragePermission(context)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun rememberNetworkStatus(): State<Boolean> {
                        val context = LocalContext.current

                        return produceState(initialValue = true) {
                            val cm = context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager
                            val callback = object : ConnectivityManager.NetworkCallback() {
                                override fun onAvailable(network: Network) {
                                    value = true
                                }

                                override fun onLost(network: Network) {
                                    value = false
                                }
                            }

                            cm.registerDefaultNetworkCallback(callback)

                            awaitDispose {
                                cm.unregisterNetworkCallback(callback)
                            }
                        }
                    }

                    @Composable
                    fun NetworkStatusBanner () {
                        val isOnline by rememberNetworkStatus ()
                        
                        AnimatedVisibility(visible = !isOnline) {
                            Surface(color = MaterialTheme.colorScheme.error) {
                                Text (
                                    text = "Network Prob...."
                                            modifier = Modifier. fillMaxWidth().padding(8dp),
                                    color = MaterialTheme.colorScheme.onError,
                                    style = MaterialTheme.typography.bodyMedium
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //don't (Not async)
                    var user by remember { mutableStateOf<User?>(null) }
                    
                    //LaunchedEffect to load async data
                    LaunchedEffect(Unit) {
                        user = repo.LoadUser()
                    }
                    
                    //do
                    val user by produceState<User?>(null) {
                        value = repo.LoadUser()
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ProduceState bridges async data with Compose state. It keeps your UI reactive, manages coroutines behind the scenes, and cleans itself up with the lifecycle. It is best used for scenarios like fetching weather, loading images, or reacting to database/Flow changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //The manual way
                    @Composable
                    fun UserProfile(userId: String) {
                        var userState by remember { mutableStateOf<UserState>(UserState.Loading) }

                        LaunchedEffect(userId) {
                            try {
                                val user = userRepository.getUser(userId)
                                userState = UserState.Success(user)
                            } catch (e: Exception) {
                                userState = UserState.Error(e.message)
                            }
                        }

                        when (userState) {
                            is UserState.Loading -> CircularProgressIndicator()
                            is UserState.Success -> UserCard(userState.user)
                            is UserState.Error -> ErrorMessage(userState.message)
                        }
                    }

                    //The produceState way
                    @Composable
                    fun UserProfile(userId: String) {
                        val userState by produceState<UserState>(
                            initialValue = UserState.Loading,
                            key1 = userId
                        ) {
                            try {
                                val user = userRepository.getUser(userId)
                                value = UserState.Success(user)
                            } catch (e: Exception) {
                                value = UserState.Error(e.message)
                            }
                        }

                        when (userState) {
                            is UserState.Loading -> CircularProgressIndicator()
                            is UserState.Success -> UserCard(userState.user)
                            is UserState.Error -> ErrorMessage(userState.message)
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState is essentially LaunchedEffect + mutableStateOf combined. It creates a State object and provides a coroutine scope where you can update its value. The state updates automatically trigger recomposition, and the coroutine is cancelled when the key changes or the composable leaves composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    // A common use case for produceState is to fetch data from an asynchronous
                    // source, such as a network API or a database, and provide that data as state
                    // to the UI. For example, consider an app that displays weather information
                    // for a city. You could use produceState to fetch and update weather data when
                    // the city changes or the screen is recomposed.
                    @Composable
                    fun WeatherScreen(cityName: String) {
                        // Use produceState to fetch weather data based on the city name
                        val weatherState = produceState<Weather?>(initialValue = null, cityName) {
                            // Fetch data for the city asynchronously
                            value = fetchWeatherData(cityName)
                        }

                        // Display the data
                        Column(
                            modifier = Modifier.fillMaxSize(),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            when (val weather = weatherState.value) {
                                null -> CircularProgressIndicator() // Show a loading spinner while data is being fetched
                                else -> Text("Temperature in $cityName: ${weather.temperature}°C")
                            }
                        }
                    }

                    // Hypothetical suspend function to fetch weather data from an API
                    suspend fun fetchWeatherData(cityName: String): Weather {
                        // Simulate network request
                        delay(1000)
                        return Weather(cityName, temperature = (15..30).random())
                    }

                    data class Weather(val city: String, val temperature: Int)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
            val shaderTime by produceState(0f) {
                while (true) {
                    withInfiniteAnimationFrameMillis {
                        value = it / 1000f * SPEED
                    }
                }
            }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class Product(val id: String, val name: String, val price: Double)

                    @Composable
                    fun RecommendedProductsScreen(userId: String) {
                        val recommendationsState = produceState<Result<PersistentList<Product>>>(initialValue = Result.Loading, key1 = userId) {
                            value = try {
                                val recommendations = fetchProductRecommendations(userId).toPersistentList() // Suspend function
                                Result.Success(recommendations)
                            } catch (e: Exception) {
                                Result.Error(e.message ?: "Unknown error")
                            }
                        }

                        when (val result = recommendationsState.value) {
                            is Result.Loading -> CircularProgressIndicator()
                            is Result.Success -> ProductList(products = result.data)
                            is Result.Error -> Text(result.message)
                        }
                    }

                    @Composable
                    fun ProductList(products: PersistentList<Product>) {
                        LazyColumn {
                            items(products, key = { product -> product.id }) { product ->
                                ProductItem(product = product)
                            }
                        }
                    }

                    @Composable
                    fun ProductItem(product: Product) {
                        Column(modifier = Modifier.padding(16.dp)) {
                            Text(text = product.name, style = MaterialTheme.typography.h6)
                            Text(text = "product.price", style = MaterialTheme.typography.body1)
                        }
                    }

                    suspend fun fetchProductRecommendations(userId: String): List<Product> {
                        // Simulate a network call to fetch recommendations
                        delay(1000) // Simulating network delay
                        return listOf(
                            Product(id = "1", name = "Supplement A", price = 29.99),
                            Product(id = "2", name = "Supplement B", price = 19.99),
                            Product(id = "3", name = "Supplement C", price = 24.99)
                        )
                    }

                    sealed class Result<out T> {
                        object Loading : Result<Nothing>()
                        data class Success<T>(val data: T) : Result<T>()
                        data class Error(val message: String) : Result<Nothing>()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //When eventId changed, load Speakers the state will be re-updated.

                    @Composable
                    fun SearchScreen(eventId: String) {
                      val uiState = produceState(initialValue = emptyList<Speaker>(), eventId) {
                        viewModel.loadSpeakers(eventId) // suspended effect
                      }

                      ItemsVerticalList(uiState.value)
                    }

                    val pokemonInfo = produceState<Resource<Pokemon>>(initialValue = Resource.Loading()) {
                            value = viewModel.getPokemonInfo(pokemonName)
                        }.value

                    @HiltViewModel
                    class PokemonDetailsViewModel @Inject constructor(
                        private val repository: PokemonRepository
                    ) : ViewModel() {

                        suspend fun getPokemonInfo(pokemonName: String): Resource<Pokemon> {
                            return repository.getPokemonInfo(pokemonName)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun ColorScreen() {
                        val selectedColor = remember { mutableStateOf(Color.White) }
                        val backgroundColor = produceState(initialValue = Color.White) {
                            selectedColor.value.let { color ->
                                val colorInt = color.toArgb()
                                val red = Color.red(colorInt)
                                val green = Color.green(colorInt)
                                val blue = Color.blue(colorInt)
                                value = Color.rgb(red, green, blue)
                            }
                        }

                        Box(
                            modifier = Modifier.fillMaxSize(),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(
                                horizontalAlignment = Alignment.CenterHorizontally,
                                verticalArrangement = Arrangement.Center
                            ) {
                                Text(
                                    text = "Select a color",
                                    fontSize = 20.sp,
                                    fontWeight = FontWeight.Bold
                                )

                                Spacer(modifier = Modifier.height(16.dp))

                                ColorSelector(
                                    selectedColor = selectedColor.value,
                                    onColorSelected = { selectedColor.value = it }
                                )
                            }

                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(backgroundColor.value)
                            )
                        }
                    }

                    @Composable
                    fun ColorSelector(
                        selectedColor: Color,
                        onColorSelected: (Color) -> Unit
                    ) {
                        Column {
                            ColorOption(
                                color = Color.White,
                                isSelected = selectedColor == Color.White,
                                onColorSelected = onColorSelected
                            )

                            ColorOption(
                                color = Color.Red,
                                isSelected = selectedColor == Color.Red,
                                onColorSelected = onColorSelected
                            )

                            ColorOption(
                                color = Color.Green,
                                isSelected = selectedColor == Color.Green,
                                onColorSelected = onColorSelected
                            )

                            ColorOption(
                                color = Color.Blue,
                                isSelected = selectedColor == Color.Blue,
                                onColorSelected = onColorSelected
                            )
                        }
                    }

                    @Composable
                    fun ColorOption(
                        color: Color,
                        isSelected: Boolean,
                        onColorSelected: (Color) -> Unit
                    ) {
                        val backgroundColor = if (isSelected) color else Color.LightGray

                        Box(
                            modifier = Modifier
                                .size(60.dp)
                                .background(backgroundColor, CircleShape)
                                .clickable { onColorSelected(color) },
                            contentAlignment = Alignment.Center
                        ) {
                            if (isSelected) {
                                Icon(
                                    imageVector = Icons.Filled.Check,
                                    contentDescription = "Selected",
                                    tint = Color.White
                                )
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Loading API Data Inside a Composable (Most Popular)
                    
                    @Composable
                    fun WeatherScreen(cityName: String) {
                        val weatherState by produceState<WeatherResult?>(initialValue = null, cityName) {
                            value = repository.loadWeather(city) // Api calling
                        }

                        if (weatherState == null) {
                              Text(""Loading....)
                        } else {
                            WeatherCard(weatherState!!)
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Observing a Flow Without ViewModel
                    
                    @Composable
                    fun NotificationsCounts(repo: NotificationRepo) {
                        val count by produceState(0) {
                            repo.notificationsFlow.collect { newCount ->
                                value = newCount
                            }
                        }

                        Text("Notifications = $count")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Running Expensive Work in the Background While UI Observes
                    
                    @Composable
                    fun CalculationView(value: Int) {
                        val calValue by produceState<String?>(null, value) {
                            value = withContext(Dispatchers.IO) {
                                heavyCalculation(file)
                            }
                        }

                        Text(calValue?: "Calcualtion ...")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Why use produceState?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "callback",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://christophybarth.medium.com/producestate-in-depth-flows-callbacks-and-cleanup-cc28ea3887a4
            KotlinCode(
                code = """
                    val location by produceState<Location?>(initialValue = null) {
                        val listener = LocationListener { value = it }
                        locationManager.register(listener)
                        awaitDispose {
                            locationManager.unregister(listener)
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When building apps, UI often depends on data that changes asynchronously. Without produceState, you would need to create your own state, launch coroutines manually, and remember to cancel them when the UI disappears. produceState simplifies this: it manages state, coroutines, and lifecycle automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This makes your code shorter, safer, and easier to read.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Converting callback-based APIs to State, Flow emissions to State, LiveData to State.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cancels and restarts automatically on key change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Handles Flow, LiveData, or any observable pattern.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Transforming any reactive stream into Compose State.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Bridging LiveData from ViewModels.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When NOT to use produceState?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When NOT to use produceState? : Use ViewModel + StateFlow or Flow + collectAsState().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When logic needs to survive configuration changes: Again → ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we want to observe multiple flows professionally, use collectAsStateWithLifecycle().",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the state is user-editable: Use remember instead.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How produceState Works in Compose.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState(initialValue = userId) Sets the initial value of the state before the coroutine completes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It launches a coroutine in a Composable to produce a value for a State.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The produced state is recomposed whenever its value changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Automatically cancels the coroutine when the Composable is no longer active.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Can take dependencies (keys) to restart the coroutine when they change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            ///https://medium.com/@ramadan123sayed
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creating a state object: It initializes a MutableState<T> with your provided initial value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Launching a coroutine: It starts a coroutine that runs your producer function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle management: The coroutine is tied to the Composition lifecycle, automatically canceling when the composable leaves the composition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key-based restarting: When any of the specified keys change, the current coroutine is canceled and a new one starts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Value updates: The producer function can update the state value using the value = assignment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "What is produceState and how does it convert non-Compose state into Compose state?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "produceState is used to convert non-Compose state, such as Flow, LiveData, or RxJava, into Compose state. It launches a coroutine scoped to the composition and updates the State object based on the emitted values from the non-Compose state source.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://medium.com/droidstack/what-is-producestate-in-jetpack-compose-real-use-cases-examples-best-practices-e228f597f08a
            //Difference between produceState vs ViewModel

            SpanText(
                text = "LaunchedEffect Vs produceState",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect with its own key.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "LaunchedEffect runs when the given key changes. So a key must be defined and has to change in composition. However, produceState comes with key, so it can run by itself.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }
    }
}